//@ts-nocheck
import { Star } from 'lucide-react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { Checkbox } from 'components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Textarea } from 'components/ui/textarea';
import { ProgramQuestionInterface } from '../components/feedback/SessionFeedback';

function Rating({ value, onChange, max, readOnly = false }: any) {
	const rating = Number(value) || 0;

	return (
		<div aria-label="Rating" className="flex justify-center gap-1" role="radiogroup">
			{Array.from({ length: max }, (_, index) => {
				const score = index + 1;
				return (
					<button
						aria-checked={rating === score}
						aria-label={`${score} star${score === 1 ? '' : 's'}`}
						className="rounded-sm p-1 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
						disabled={readOnly}
						key={score}
						onClick={() => onChange?.(score)}
						role="radio"
						type="button"
					>
						<Star className={score <= rating ? 'size-6 fill-current' : 'size-6'} />
					</button>
				);
			})}
		</div>
	);
}

export function answerField(
	question: ProgramQuestionInterface,
	index: number,
	control: Control<FieldValues> | undefined,
	feedback: { question_id: number; text: string | string[] | null; value: number | null } | null | undefined
) {
	return {
		text: (
			<Controller
				control={control}
				defaultValue={feedback ? feedback.text : ''}
				name={`feedback.${index}.text` as `${string}.${number}`}
				render={({ field }) => <Textarea {...field} placeholder="Your Answer" rows={4} />}
			/>
		),
		options: (
			<Controller
				control={control}
				defaultValue={feedback ? feedback.text : ''}
				name={`feedback.${index}.text` as `${string}.${number}`}
				render={({ field }) => (
					<RadioGroup name={field.name} onValueChange={field.onChange} value={field.value ?? ''}>
						{question.options.map((option: string) => (
							<label className="flex items-center gap-2 text-sm text-gray-900" key={option}>
								<RadioGroupItem value={option} />
								{option}
							</label>
						))}
					</RadioGroup>
				)}
			/>
		),
		stars: (
			<Controller
				control={control}
				defaultValue={feedback ? feedback.value : null}
				name={`feedback.${index}.value` as `${string}.${number}`}
				render={({ field }) => <Rating max={question.stars || 5} onChange={field.onChange} value={field.value} />}
			/>
		),
		checkboxes: (
			<Controller
				control={control}
				defaultValue={feedback ? feedback.text : ''}
				name={`feedback.${index}.text` as `${string}.${number}`}
				render={({ field }) => {
					const selected = Array.isArray(field.value) ? field.value : [];
					return (
						<div className="grid gap-2">
							{question.options.map((option: string) => (
								<label className="flex items-center gap-2 text-sm text-gray-900" key={option}>
									<Checkbox
										checked={selected.includes(option)}
										onCheckedChange={(checked) =>
											field.onChange(checked ? [...selected, option] : selected.filter((value) => value !== option))
										}
									/>
									{option}
								</label>
							))}
						</div>
					);
				}}
			/>
		),
	}[question.category as 'text' | 'options' | 'stars' | 'checkboxes'];
}

export function ShowAnswer(question: ProgramQuestionInterface, feedback: any) {
	const selected = Array.isArray(feedback?.text) ? feedback.text : feedback?.text ? [feedback.text] : [];

	return {
		text: <Textarea defaultValue={feedback?.text ?? ''} readOnly rows={4} />,
		options: (
			<RadioGroup disabled value={feedback?.text ?? ''}>
				{question.options.map((option: string) => (
					<label className="flex items-center gap-2 text-sm text-gray-900" key={option}>
						<RadioGroupItem value={option} />
						{option}
					</label>
				))}
			</RadioGroup>
		),
		stars: <Rating max={question.stars || 5} readOnly value={feedback?.value} />,
		checkboxes: (
			<div className="grid gap-2">
				{question.options.map((option: string) => (
					<label className="flex items-center gap-2 text-sm text-gray-900" key={option}>
						<Checkbox checked={selected.includes(option)} disabled />
						{option}
					</label>
				))}
			</div>
		),
	}[question.category as 'text' | 'options' | 'stars' | 'checkboxes'];
}

export function ShowSubmittedAnswer(question: any) {
	return {
		text: <Textarea defaultValue={question.text ?? ''} readOnly rows={4} />,
		options: <Textarea defaultValue={question.text ?? ''} readOnly rows={4} />,
		stars: <Rating max={question.stars || 5} readOnly value={question.value} />,
		checkboxes: <Textarea defaultValue={question.text ?? ''} readOnly rows={4} />,
	}[question.category as 'text' | 'options' | 'stars' | 'checkboxes'];
}
